import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { useGroupLessons, useGroupZoomRecordings } from 'core/hooks';
import { LoadingIndicator } from 'atoms';

import * as S from '../../FakturowniaInvoices/FakturowniaInvoices.styled';
import { ZoomAutoScheduleCard } from '../parts/ZoomAutoScheduleCard';
import { ZoomRecordingsList } from '../parts/ZoomRecordingsList';

interface IZoomTabProps {
  groupId: string;
}

/**
 * ZoomTab — auto-process schedule card + recordings list for one
 * group. The lessons query is shared with ScheduleTab (cache will
 * usually hit), the recordings list defaults to the last 90 days so
 * older archived sessions don't drown the most recent ones.
 */
export const ZoomTab = memo(({ groupId }: IZoomTabProps) => {
  const { t } = useTranslation();
  const {
    group,
    loading: groupLoading,
    refetch: refetchGroup,
  } = useGroupLessons(groupId);

  const dateRange = useMemo(() => {
    const dateTo = moment().format('YYYY-MM-DD');
    const dateFrom = moment().subtract(90, 'days').format('YYYY-MM-DD');
    return { dateFrom, dateTo };
  }, []);

  const {
    recordings,
    loading: recordingsLoading,
    refetch: refetchRecordings,
  } = useGroupZoomRecordings({
    groupId,
    dateFrom: dateRange.dateFrom,
    dateTo: dateRange.dateTo,
    first: 100,
  });

  const onSynced = () => {
    refetchGroup();
    refetchRecordings();
  };

  if (groupLoading && !group) {
    return (
      <S.CenterLoader>
        <LoadingIndicator />
      </S.CenterLoader>
    );
  }

  if (!group) {
    return <S.EmptyState>{t('pages.groupDetail.zoom.empty')}</S.EmptyState>;
  }

  return (
    <S.DimWhenFetching fetching={recordingsLoading}>
      <ZoomAutoScheduleCard group={group} onSynced={onSynced} />
      <ZoomRecordingsList recordings={recordings} loading={recordingsLoading} />
    </S.DimWhenFetching>
  );
});
